import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { useAuth } from '@/context/AuthContext';
import { ProductUpdatePopupView, type PopupUpdate } from '@/components/product-updates/ProductUpdatePopupView';

interface ProductUpdate extends PopupUpdate {
  id: number;
}

/**
 * ProductUpdatePopup
 *
 * Self-fetching member wrapper. Checks for unseen product updates on mount;
 * if one exists, shows the popup once. Dismissing marks the update as seen
 * so it never re-appears. The layout lives in ProductUpdatePopupView so the
 * admin preview can render it without fetching or marking anything seen.
 */
export default function ProductUpdatePopup() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Only check for unseen updates when user is logged in
  // Include user.id in query key so each user gets their own cache entry
  // Poll every 2 minutes for new updates (only if not dismissed)
  const { data: unseenUpdate } = useQuery<ProductUpdate | null>({
    queryKey: ['/api/product-updates/unseen', user?.id],
    queryFn: async () => {
      const result = await apiRequest<ProductUpdate | { id: null }>('GET', '/api/product-updates/unseen');
      return result?.id ? (result as ProductUpdate) : null;
    },
    enabled: !!user && !dismissed,
    staleTime: 0, // Always check on mount
    gcTime: 0, // Don't cache across sessions
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: !dismissed ? 2 * 60 * 1000 : false, // Poll every 2 minutes when active
  });

  // Reset dismissed flag when user changes (new login)
  useEffect(() => {
    if (user?.id) {
      setDismissed(false);
    }
  }, [user?.id]);

  // Auto-open when unseen update arrives (only once per session)
  useEffect(() => {
    if (unseenUpdate && unseenUpdate.id && !dismissed) {
      // Small delay so the app finishes rendering first
      const timer = setTimeout(() => setOpen(true), 800);
      return () => clearTimeout(timer);
    }
  }, [unseenUpdate, dismissed]);

  const markSeenMutation = useMutation({
    mutationFn: (id: number) => apiRequest('POST', `/api/product-updates/${id}/seen`),
  });

  const handleDismiss = () => {
    setOpen(false);
    setDismissed(true);
    if (unseenUpdate) {
      markSeenMutation.mutate(unseenUpdate.id);
    }
    // Clear the cached unseen data so the popup doesn't flash on re-render
    queryClient.setQueryData(['/api/product-updates/unseen', user?.id], null);
  };

  if (!unseenUpdate || !unseenUpdate.id) return null;

  return <ProductUpdatePopupView open={open} update={unseenUpdate} onDismiss={handleDismiss} />;
}
